/**
 * aiml-page.js — Sanity integration for the AI & ML V4 page
 */

import { client, urlFor, getSanityEditLink, resolveInternalLink } from './sanity-client';

const QUERY = `*[_type == "aimlPage" && slug.current == "aiml"][0]{
  _id, title, slug,
  showHero, showModelDev, showCapabilities, showCaseStudies, showCta,
  heroDescription,
  mdHeading, mdLede,
  capHeading, capLede,
  capTiles[]{ _key, ix, heading, body },
  "caseStudies": caseStudies[@->visibility != false]->{ _id, title, "slug": slug.current, image, description },
  ctaHeadline, ctaLede, ctaLocations
}`;

function parseAccent(text) {
  if (!text) return '';
  return text.replace(/\*([^*]+)\*/g, '<em style="font-style:normal;color:var(--ai4-violet);font-weight:700">$1</em>');
}

function setField(attr, value, html = false) {
  const el = document.querySelector(`[data-ai4="${attr}"]`);
  if (!el || !value) return;
  if (html) el.innerHTML = value;
  else el.textContent = value;
}

function setVisible(id, visible) {
  const el = document.getElementById(id);
  if (!el) return;
  el.style.display = visible === false ? 'none' : '';
}

function attachRevealObserver(els) {
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
  els.forEach(el => io.observe(el));
}

function renderHero(data, docId) {
  if (data.title) {
    const el = document.querySelector('[data-ai4="title"]');
    if (el) el.innerHTML = `${data.title}<span class="dot">.</span>`;
  }
  setField('heroDescription', data.heroDescription);

  const section = document.getElementById('ai4-hero');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'aimlPage', 'heroDescription', 'Edit Hero'));
  }
}

function renderModelDev(data, docId) {
  if (data.mdHeading) {
    const el = document.querySelector('[data-ai4="mdHeading"]');
    if (el) el.innerHTML = parseAccent(data.mdHeading);
  }
  setField('mdLede', data.mdLede);

  const section = document.getElementById('ai4-model-dev');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'aimlPage', 'mdHeading', 'Edit Model Dev'));
  }
}

function renderCapabilities(data, docId) {
  if (data.capHeading) {
    const el = document.querySelector('[data-ai4="capHeading"]');
    if (el) el.innerHTML = parseAccent(data.capHeading);
  }
  setField('capLede', data.capLede);

  const section = document.getElementById('ai4-capabilities');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'aimlPage', 'capHeading', 'Edit Capabilities'));
  }
}

function renderCaseStudies(caseStudies, docId) {
  const section = document.getElementById('ai4-case-studies');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'aimlPage', 'caseStudies', 'Edit Case Studies'));
  }

  const grid = document.getElementById('ai4-cs-grid');
  if (!grid || !caseStudies || !caseStudies.length) return;

  const artBgs = [
    `radial-gradient(120% 80% at 90% 90%, rgba(126,91,239,.30), transparent 55%),
     radial-gradient(60% 60% at 10% 10%, rgba(31,184,166,.18), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #1A1525 60%, #0A0B0D 100%)`,
    `radial-gradient(120% 80% at 10% 90%, rgba(31,184,166,.25), transparent 55%),
     radial-gradient(60% 60% at 90% 10%, rgba(126,91,239,.18), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #14181E 60%, #0A0B0D 100%)`,
    `radial-gradient(120% 80% at 50% 100%, rgba(126,91,239,.22), transparent 55%),
     radial-gradient(60% 60% at 50% 0%, rgba(224,168,46,.15), transparent 60%),
     linear-gradient(135deg, #0F1114 0%, #161420 60%, #0A0B0D 100%)`,
  ];

  const glyphs = [
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <rect x="40" y="70" width="80" height="60" rx="6"/>
       <path d="M120 90 L160 68 V132 L120 110 Z"/>
       <circle cx="80" cy="100" r="14"/>
       <circle cx="80" cy="100" r="5" fill="currentColor" stroke="none"/>
     </svg>`,
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <rect x="30" y="40" width="140" height="120" rx="4"/>
       <line x1="100" y1="40" x2="100" y2="160"/>
       <line x1="30" y1="100" x2="170" y2="100"/>
       <circle cx="100" cy="100" r="22"/>
       <circle cx="56" cy="68" r="6" fill="currentColor" stroke="none"/>
       <circle cx="144" cy="132" r="6" fill="currentColor" stroke="none"/>
     </svg>`,
    `<svg viewBox="0 0 200 200" fill="none" stroke="currentColor" stroke-width="1.4">
       <rect x="36" y="46" width="128" height="108" rx="6"/>
       <line x1="54" y1="76" x2="146" y2="76"/>
       <line x1="54" y1="96" x2="146" y2="96"/>
       <line x1="54" y1="116" x2="120" y2="116"/>
       <circle cx="148" cy="148" r="20"/>
       <path d="M140 148 L146 154 L156 142"/>
     </svg>`,
  ];

  grid.innerHTML = caseStudies.map((cs, i) => {
    const href = cs.title
      ? resolveInternalLink(`/case-studies/case-study.html?title=${encodeURIComponent(cs.title)}`)
      : '#';
    const hasImage = cs.image && cs.image.asset;
    const imgUrl = hasImage ? urlFor(cs.image).width(800).quality(80).url() : '';
    const artStyle = hasImage
      ? `style="background-image:linear-gradient(180deg, rgba(0,0,0,.15), rgba(0,0,0,.65)), url('${imgUrl}');background-size:cover;background-position:center"`
      : `style="background:${artBgs[i % artBgs.length]}"`;
    return `
      <a href="${href}" class="ai4-cs-card" data-reveal style="--rd:${i * 120}ms">
        <div class="art" ${artStyle}>
          <div class="art-grid"></div>
          ${hasImage ? '' : `<div class="art-glyph">${glyphs[i % glyphs.length]}</div>`}
        </div>
        <div class="read" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
        </div>
        <span class="ribbon"></span>
        <div class="meta">
          <span class="cs-tag">AI &amp; ML</span>
          <h3>${cs.title || ''}</h3>
        </div>
      </a>`;
  }).join('');

  attachRevealObserver(grid.querySelectorAll('[data-reveal]'));
}

function renderCta(data, docId) {
  if (data.ctaHeadline) {
    const el = document.querySelector('[data-ai4="ctaHeadline"]');
    if (el) el.innerHTML = parseAccent(data.ctaHeadline);
  }
  setField('ctaLede', data.ctaLede);
  setField('ctaLocations', data.ctaLocations);

  const section = document.getElementById('ai4-cta');
  if (section && docId) {
    section.classList.add('position-relative');
    section.insertAdjacentHTML('afterbegin',
      getSanityEditLink(docId, 'aimlPage', 'ctaHeadline', 'Edit CTA'));
  }
}

function initNavbarScroll() {
  const navbar = document.querySelector('.navbar');
  if (!navbar) return;
  function onScroll() {
    if (window.scrollY > 50) {
      navbar.classList.add('navbar-v3-scrolled');
      navbar.classList.remove('navbar-v3-transparent');
    } else {
      navbar.classList.remove('navbar-v3-scrolled');
      navbar.classList.add('navbar-v3-transparent');
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

function initStaticReveal() {
  const wrap = document.getElementById('ai4-root');
  if (wrap) wrap.classList.add('js-reveal-ready');
  const els = document.querySelectorAll('.ai4-wrap [data-reveal]');
  attachRevealObserver(els);
  setTimeout(() => {
    document.querySelectorAll('.ai4-wrap [data-reveal]:not(.in)').forEach(el => el.classList.add('in'));
  }, 1200);
}

function initCsRail() {
  const rail = document.getElementById('ai4-cs-rail');
  const grid = document.getElementById('ai4-cs-grid');
  if (!rail || !grid) return;
  const prev = rail.querySelector('.ai4-cs-arrow.prev');
  const next = rail.querySelector('.ai4-cs-arrow.next');

  function updateArrows() {
    if (prev) prev.disabled = grid.scrollLeft <= 0;
    if (next) next.disabled = grid.scrollLeft + grid.clientWidth >= grid.scrollWidth - 1;
  }
  function checkOverflow() {
    rail.classList.toggle('has-overflow', grid.scrollWidth > grid.clientWidth);
    updateArrows();
  }
  if (prev) prev.addEventListener('click', () => {
    const card = grid.querySelector('.ai4-cs-card');
    grid.scrollBy({ left: -(card ? card.offsetWidth + 20 : 300), behavior: 'smooth' });
  });
  if (next) next.addEventListener('click', () => {
    const card = grid.querySelector('.ai4-cs-card');
    grid.scrollBy({ left: card ? card.offsetWidth + 20 : 300, behavior: 'smooth' });
  });
  grid.addEventListener('scroll', updateArrows, { passive: true });
  new ResizeObserver(checkOverflow).observe(grid);
  checkOverflow();
}

document.addEventListener('DOMContentLoaded', async () => {
  initNavbarScroll();
  initStaticReveal();
  initCsRail();

  let data;
  try {
    data = await client.fetch(QUERY);
  } catch (err) {
    console.warn('[aiml-page] Sanity fetch failed — using static HTML fallback.', err);
    return;
  }

  if (!data) {
    console.warn('[aiml-page] No Sanity document found for slug "aiml".');
    return;
  }

  const docId = data._id;

  setVisible('ai4-hero',         data.showHero);
  setVisible('ai4-model-dev',    data.showModelDev);
  setVisible('ai4-capabilities', data.showCapabilities);
  setVisible('ai4-case-studies', data.showCaseStudies);
  setVisible('ai4-cta',          data.showCta);

  if (data.showHero !== false)          renderHero(data, docId);
  if (data.showModelDev !== false)      renderModelDev(data, docId);
  if (data.showCapabilities !== false)  renderCapabilities(data, docId);
  if (data.showCaseStudies !== false) {
    renderCaseStudies(data.caseStudies || [], docId);
    if (data.caseStudies && data.caseStudies.length) initCsRail();
  }
  if (data.showCta !== false)           renderCta(data, docId);
});
